import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import {
  createDietPlan,
  generatePlan,
  listDietPlans,
  updateDietPlan,
} from '../../api/planner.js';
import type { DietPlan, GenerateInput } from '../../api/plannerTypes.js';
import { useToast } from '../../hooks/useToast.js';

export interface PlannerConfigProps {
  onGenerated: () => void;
}

interface FormState {
  planId: number | null;
  name: string;
  targetKcal: number;
  targetProtein: number;
  targetCarbs: number;
  targetFat: number;
  distBreakfast: number;
  distLunch: number;
  distDinner: number;
}

const defaultForm: FormState = {
  planId: null,
  name: '',
  targetKcal: 2000,
  targetProtein: 100,
  targetCarbs: 200,
  targetFat: 70,
  distBreakfast: 0.25,
  distLunch: 0.4,
  distDinner: 0.35,
};

function todayIso(): string {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function distSum(s: FormState): number {
  return s.distBreakfast + s.distLunch + s.distDinner;
}

export function PlannerConfig({ onGenerated }: PlannerConfigProps): ReactElement {
  const [plans, setPlans] = useState<DietPlan[]>([]);
  const [form, setForm] = useState<FormState>(defaultForm);
  const [startDate, setStartDate] = useState<string>(todayIso());
  const [days, setDays] = useState<number>(7);
  const [useMealprep, setUseMealprep] = useState<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await listDietPlans();
        if (cancelled) return;
        setPlans(Array.isArray(list) ? list : []);
        if (list.length > 0 && form.planId === null) {
          const p = list[0]!;
          setForm((f) => ({
            ...f,
            planId: p.id,
            name: p.name,
            targetKcal: p.target_kcal_day,
            targetProtein: p.target_protein_day,
            targetCarbs: p.target_carbs_day,
            targetFat: p.target_fat_day,
            distBreakfast: p.kcal_distribution_breakfast,
            distLunch: p.kcal_distribution_lunch,
            distDinner: p.kcal_distribution_dinner,
          }));
        }
      } catch (e) {
        if (!cancelled) toast.push('error', e instanceof Error ? e.message : 'Failed to load plans');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sumWarn = useMemo(() => Math.abs(distSum(form) - 1) > 0.02, [form]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function loadPlanIntoForm(p: DietPlan): void {
    setForm({
      planId: p.id,
      name: p.name,
      targetKcal: p.target_kcal_day,
      targetProtein: p.target_protein_day,
      targetCarbs: p.target_carbs_day,
      targetFat: p.target_fat_day,
      distBreakfast: p.kcal_distribution_breakfast,
      distLunch: p.kcal_distribution_lunch,
      distDinner: p.kcal_distribution_dinner,
    });
  }

  async function handleSave(): Promise<void> {
    if (!form.name.trim() || form.targetKcal <= 0) {
      toast.push('error', 'Name and a positive kcal target are required');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        target_kcal_day: form.targetKcal,
        target_protein_day: form.targetProtein,
        target_carbs_day: form.targetCarbs,
        target_fat_day: form.targetFat,
        kcal_distribution_breakfast: form.distBreakfast,
        kcal_distribution_lunch: form.distLunch,
        kcal_distribution_dinner: form.distDinner,
      };
      const res = form.planId === null
        ? await createDietPlan(payload)
        : await updateDietPlan(form.planId, payload);
      setPlans((ps) => {
        const existing = ps.findIndex((p) => p.id === res.plan.id);
        if (existing >= 0) {
          const copy = ps.slice();
          copy[existing] = res.plan;
          return copy;
        }
        return [...ps, res.plan];
      });
      setForm((f) => ({ ...f, planId: res.plan.id }));
      if (res.warnings.length > 0) {
        toast.push('info', res.warnings[0]!);
      } else {
        toast.push('success', 'Plan saved');
      }
    } catch (e) {
      toast.push('error', e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleGenerate(): Promise<void> {
    let planId = form.planId;
    if (planId === null) {
      toast.push('error', 'Save the plan before generating');
      return;
    }
    setBusy(true);
    try {
      const input: GenerateInput = {
        diet_plan_id: planId,
        start_date: startDate,
        days,
        use_mealprep: useMealprep,
      };
      await generatePlan(input);
      toast.push('success', 'Plan generated');
      onGenerated();
    } catch (e) {
      toast.push('error', e instanceof Error ? e.message : 'Generate failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      className="flex flex-col gap-4 p-4"
      data-testid="planner-config"
    >
      <h2 className="text-lg font-semibold" style={{ color: 'var(--text-main)' }}>
        Diet Plan Configuration
      </h2>

      <div className="flex flex-col gap-2">
        <label className="text-sm" style={{ color: 'var(--text-muted)' }} htmlFor="planner-plan-select">
          Existing plan
        </label>
        <select
          id="planner-plan-select"
          data-testid="planner-plan-select"
          className="rounded-md border px-3 py-2 text-sm"
          style={{ background: 'var(--bg-surface)', borderColor: 'var(--border)', color: 'var(--text-main)' }}
          value={form.planId ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            const id = v === '' ? null : Number.parseInt(v, 10);
            const p = plans.find((x) => x.id === id);
            if (p) loadPlanIntoForm(p);
            else setForm(defaultForm);
          }}
        >
          <option value="">— New plan —</option>
          {plans.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm" style={{ color: 'var(--text-muted)' }} htmlFor="planner-name">Plan name</label>
        <input
          id="planner-name"
          data-testid="planner-name"
          type="text"
          className="rounded-md border px-3 py-2 text-sm"
          style={{ background: 'var(--bg-surface)', borderColor: 'var(--border)', color: 'var(--text-main)' }}
          value={form.name}
          onChange={(e) => setField('name', e.target.value)}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs" style={{ color: 'var(--text-muted)' }} htmlFor="planner-kcal">kcal / day</label>
          <input
            id="planner-kcal"
            data-testid="planner-kcal"
            type="number"
            min={1}
            className="rounded-md border px-3 py-2 text-sm"
            style={{ background: 'var(--bg-surface)', borderColor: 'var(--border)', color: 'var(--text-main)' }}
            value={form.targetKcal}
            onChange={(e) => setField('targetKcal', Number(e.target.value))}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs" style={{ color: 'var(--text-muted)' }} htmlFor="planner-protein">Protein g</label>
          <input
            id="planner-protein"
            data-testid="planner-protein"
            type="number"
            min={0}
            className="rounded-md border px-3 py-2 text-sm"
            style={{ background: 'var(--bg-surface)', borderColor: 'var(--border)', color: 'var(--text-main)' }}
            value={form.targetProtein}
            onChange={(e) => setField('targetProtein', Number(e.target.value))}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs" style={{ color: 'var(--text-muted)' }} htmlFor="planner-carbs">Carbs g</label>
          <input
            id="planner-carbs"
            data-testid="planner-carbs"
            type="number"
            min={0}
            className="rounded-md border px-3 py-2 text-sm"
            style={{ background: 'var(--bg-surface)', borderColor: 'var(--border)', color: 'var(--text-main)' }}
            value={form.targetCarbs}
            onChange={(e) => setField('targetCarbs', Number(e.target.value))}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs" style={{ color: 'var(--text-muted)' }} htmlFor="planner-fat">Fat g</label>
          <input
            id="planner-fat"
            data-testid="planner-fat"
            type="number"
            min={0}
            className="rounded-md border px-3 py-2 text-sm"
            style={{ background: 'var(--bg-surface)', borderColor: 'var(--border)', color: 'var(--text-main)' }}
            value={form.targetFat}
            onChange={(e) => setField('targetFat', Number(e.target.value))}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-sm" style={{ color: 'var(--text-muted)' }}>kcal distribution (should sum to ~1)</span>
        <div className="grid grid-cols-3 gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-xs" htmlFor="planner-dist-b">Breakfast</label>
            <input
              id="planner-dist-b"
              data-testid="planner-dist-b"
              type="number"
              step={0.05}
              min={0}
              max={1}
              className="rounded-md border px-2 py-1 text-sm"
              style={{ background: 'var(--bg-surface)', borderColor: 'var(--border)', color: 'var(--text-main)' }}
              value={form.distBreakfast}
              onChange={(e) => setField('distBreakfast', Number(e.target.value))}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs" htmlFor="planner-dist-l">Lunch</label>
            <input
              id="planner-dist-l"
              data-testid="planner-dist-l"
              type="number"
              step={0.05}
              min={0}
              max={1}
              className="rounded-md border px-2 py-1 text-sm"
              style={{ background: 'var(--bg-surface)', borderColor: 'var(--border)', color: 'var(--text-main)' }}
              value={form.distLunch}
              onChange={(e) => setField('distLunch', Number(e.target.value))}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs" htmlFor="planner-dist-d">Dinner</label>
            <input
              id="planner-dist-d"
              data-testid="planner-dist-d"
              type="number"
              step={0.05}
              min={0}
              max={1}
              className="rounded-md border px-2 py-1 text-sm"
              style={{ background: 'var(--bg-surface)', borderColor: 'var(--border)', color: 'var(--text-main)' }}
              value={form.distDinner}
              onChange={(e) => setField('distDinner', Number(e.target.value))}
            />
          </div>
        </div>
        {sumWarn ? (
          <span data-testid="planner-dist-warn" className="text-xs" style={{ color: 'var(--accent)' }}>
            Sum is {distSum(form).toFixed(2)}; expected ~1.00
          </span>
        ) : null}
      </div>

      <button
        type="button"
        data-testid="planner-save"
        onClick={() => void handleSave()}
        disabled={busy}
        className="flex min-h-[44px] items-center justify-center rounded-md border px-4 text-sm font-medium disabled:opacity-50"
        style={{
          borderColor: 'var(--accent)',
          color: 'var(--accent)',
          background: 'transparent',
        }}
      >
        Save plan
      </button>

      <div className="mt-2 flex flex-col gap-2 border-t pt-4" style={{ borderColor: 'var(--border)' }}>
        <span className="text-sm font-medium" style={{ color: 'var(--text-main)' }}>Generate</span>
        <div className="grid grid-cols-3 gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-xs" htmlFor="planner-start">Start date</label>
            <input
              id="planner-start"
              data-testid="planner-start"
              type="date"
              className="rounded-md border px-2 py-1 text-sm"
              style={{ background: 'var(--bg-surface)', borderColor: 'var(--border)', color: 'var(--text-main)' }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs" htmlFor="planner-days">Days</label>
            <input
              id="planner-days"
              data-testid="planner-days"
              type="number"
              min={1}
              max={14}
              className="rounded-md border px-2 py-1 text-sm"
              style={{ background: 'var(--bg-surface)', borderColor: 'var(--border)', color: 'var(--text-main)' }}
              value={days}
              onChange={(e) => setDays(Math.max(1, Math.min(14, Number(e.target.value) || 1)))}
            />
          </div>
          <label className="flex flex-col gap-1 text-xs" style={{ color: 'var(--text-muted)' }}>
            Meal-Prep
            <input
              data-testid="planner-mealprep"
              type="checkbox"
              className="h-5 w-5"
              checked={useMealprep}
              onChange={(e) => setUseMealprep(e.target.checked)}
            />
          </label>
        </div>
        <button
          type="button"
          data-testid="planner-generate"
          onClick={() => void handleGenerate()}
          disabled={busy || form.planId === null}
          className="flex min-h-[44px] items-center justify-center rounded-md px-4 text-sm font-medium disabled:opacity-50"
          style={{
            background: 'var(--accent)',
            color: 'var(--bg-surface)',
          }}
        >
          Generate Plan
        </button>
      </div>
    </section>
  );
}